import { STATUS_BADGE_VARIANT } from '@vueron/kiska';
import type { Modeling } from '@/type';

export enum TASK_WORKSTATE {
  NONE = 'None',
  PENDING = 'Pending',
  READY = 'Ready',
  PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  FAIL = 'Fail',
}

export enum TASK_DISTRIBUTE_METHOD {
  NORMAL = 'Normal',
  BALANCE = 'Balance',
  RANGE = 'Range',
  RATIO = 'Ratio',
}

export const TASK_WORKSTATE_TRANSLATION_MAPPER: {
  [K in TASK_WORKSTATE]: keyof TranslationMap;
} = {
  [TASK_WORKSTATE.NONE]: 'task.noneState.recompound',
  [TASK_WORKSTATE.PENDING]: 'task.pendingState.accomplicity',
  [TASK_WORKSTATE.READY]: 'task.readyState.reindict',
  [TASK_WORKSTATE.PROGRESS]: 'task.progressState.metazoal',
  [TASK_WORKSTATE.COMPLETED]: 'task.completedState.chemicalization',
  [TASK_WORKSTATE.FAIL]: 'task.failState.antipyrine',
} as const;

export enum TASK_FRAME_WORKSTATE {
  WORKING = 'Working',
  PREWORK = 'Prework',
  REVIEW = 'Review',
  REVIEWING = 'Reviewing',
  DONE = 'Done',
  REJECT = 'Reject',
  VIEW_ONLY = 'ViewOnly',
}

export enum TASK_FRAME_WORK_TYPE {
  REVIEW = 'Review',
  REWORK = 'Rework',
  WORK = 'Work',
}

/**
 * Task Detail 벌크 툴바에서 제공하는 프레임 일괄 액션 종류입니다.
 * REDISTRIBUTE 는 작업 단계 재배정으로 별도 모달에서 처리하며 카운트를 표시하지 않습니다.
 */
export enum TASK_FRAME_BULK_ACTION {
  HANDOVER = 'handover',
  REDISTRIBUTE = 'redistribute',
  REJECT = 'reject',
  VERIFY = 'verify',
}

export enum TASK_OPERATION_TYPE {
  AI_ANNOTATION = 'AIAnnotation',
  EXPORT_LABEL = 'ExportLabel',
  MODEL_TRAINING = 'ModelTraining',
  MODEL_DEPLOY = 'ModelDeploy',
  REDISTRIBUTION = 'Redistribution',
}

export enum TASK_OPERATION_STATE {
  RUNNING = 'Running',
  COMPLETED = 'Completed',
  CANCELED = 'Canceled',
  FAILED = 'Failed',
}

export const TASK_OPERATION_STATE_STATUS_MAPPER = {
  [TASK_OPERATION_STATE.CANCELED]: STATUS_BADGE_VARIANT.NOT_STARTED,
  [TASK_OPERATION_STATE.COMPLETED]: STATUS_BADGE_VARIANT.COMPLETED,
  [TASK_OPERATION_STATE.FAILED]: STATUS_BADGE_VARIANT.FAILED,
  [TASK_OPERATION_STATE.RUNNING]: STATUS_BADGE_VARIANT.IN_PROGRESS,
} as const;

export const TASK_OPERATION_STATUS_BADGE_TEXT_MAPPER = {
  [STATUS_BADGE_VARIANT.NOT_STARTED]: 'Not Started',
  [STATUS_BADGE_VARIANT.COMPLETED]: 'Available',
  [STATUS_BADGE_VARIANT.FAILED]: 'Failed',
  [STATUS_BADGE_VARIANT.IN_PROGRESS]: 'In Progress',
} as const;

export const TASK_FRAME_WORK_STATE_TRANSLATION_KEY_MAPPER = {
  working: 'frame.workingState.fireworm',
  prework: 'frame.preworkState.panegyrizer',
  review: 'frame.reviewState.infeasibleness',
  reviewing: 'frame.reviewingState.preteressential',
  done: 'frame.doneState.hypoendocrinism',
  reject: 'frame.rejectState.swindler',
} as const;

export type TASK_FRAME_STATE_TRANSLATION_KEY_MAPPER_KEY =
  keyof typeof TASK_FRAME_WORK_STATE_TRANSLATION_KEY_MAPPER;

export const STATUS_COLOR_MAP: {
  [key in
    | DisplayedStatus
    | Modeling['status']
    | Modeling['deployStatus']
    | keyof Pick<
        TranslationMap,
        | 'common.notStarted.disciple'
        | 'common.inProgress.hypopial'
        | 'common.completed.boston'
      >
    | TASK_WORKSTATE
    | TASK_OPERATION_STATE
    | STATUS_BADGE_VARIANT]: string;
} = {
  [TASK_WORKSTATE.NONE]: '#B3B3B3',
  [TASK_WORKSTATE.FAIL]: '#F46057',
  [TASK_WORKSTATE.PENDING]: '#B3B3B3',
  [TASK_WORKSTATE.READY]: '#B3B3B3',
  [TASK_WORKSTATE.PROGRESS]: '#2CCB6F',
  [TASK_WORKSTATE.COMPLETED]: '#2A58F6',

  [STATUS_BADGE_VARIANT.NOT_STARTED]: '#B3B3B3',
  [STATUS_BADGE_VARIANT.IN_PROGRESS]: '#2CCB6F',
  [STATUS_BADGE_VARIANT.COMPLETED]: '#2A58F6',
  [STATUS_BADGE_VARIANT.FAILED]: '#F46057',
  [STATUS_BADGE_VARIANT.REJECTED]: '#DA645D',
  [STATUS_BADGE_VARIANT.AI_ANNO]: '#7DCC49',
  [STATUS_BADGE_VARIANT.IN_REVIEW]: '#AD55FF',
  [STATUS_BADGE_VARIANT.READY]: '#82A8E2',
  [STATUS_BADGE_VARIANT.SUBMITTED]: '#4AACDC',

  [TASK_OPERATION_STATE.CANCELED]: '#82A8E2',
  // [TASK_OPERATION_STATE.COMPLETED]: '#4AACDC',
  [TASK_OPERATION_STATE.FAILED]: '#F46057',
  [TASK_OPERATION_STATE.RUNNING]: '#2CCB6F',

  'Not started': '#B3B3B3',
  'View Only': '#B3B3B3',
  'In progress': '#2CCB6F',
  Training: '#2CCB6F',
  'In review': '#AD55FF',
  Available: '#2A58F6',
  Rejected: '#DA645D',
  // Failed: '#F46057',
  Failure: '#F46057',
  'AI-anno': '#7DCC49',
  Submitted: '#4AACDC',
  Deploying: '#2CCB6F',
  Deployed: '#2A58F6',
  DeployFailure: '#F46057',
  DeployPending: '#B3B3B3',

  'common.notStarted.disciple': '#B3B3B3',
  'common.inProgress.hypopial': '#2CCB6F',
  'common.completed.boston': '#2A58F6',
} as const;
